import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Pagination,
  CircularProgress,
  Paper,
  Divider,
  Chip,
  SelectChangeEvent,
  Tooltip,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useApiGet } from '../../utils/httpClientTs';
import {
  getMarketplaceItems,
  getMarketplaceCategories,
  getMarketplaceStatuses,
} from '../../endpoints/marketplace';
import { MarketplaceItem } from '../../types/marketplace';
import { MarketplaceItemCard } from '../../Components/Marketplace';
import { roles, applications } from '../../utils/variables';
import AlertItem from '../../Components/AlertItem';

// Persona checking functions
const getMarketplacePersona = (): 'shopper' | 'creator' | 'approver' => {
  const userRole = localStorage.getItem('role');
  const userApps = localStorage.getItem('apps');

  // Check if user is an approver (Admin role or has MarketplaceAdmin app)
  if (
    userRole === roles.Admin ||
    (userApps && userApps.includes(applications.MarketplaceAdmin))
  ) {
    return 'approver';
  }

  // Check if user has created items (we'll determine this based on API response)
  // For now, we'll assume all logged-in users can be creators
  return 'creator';
};

const canViewStatusFilter = (persona: string): boolean => {
  return persona === 'approver';
};

const canViewMyItemsFilter = (persona: string): boolean => {
  return persona === 'creator' || persona === 'approver';
};

const MarketplaceList: React.FC = () => {
  const navigate = useNavigate();
  const persona = getMarketplacePersona();

  // Load filters from localStorage on component mount
  const [searchTerm, setSearchTerm] = useState(() => {
    return localStorage.getItem(`marketplace_${persona}_searchTerm`) || '';
  });
  const [delayedSearchTerm, setDelayedSearchTerm] = useState(() => {
    return localStorage.getItem(`marketplace_${persona}_searchTerm`) || '';
  });
  const [selectedCategory, setSelectedCategory] = useState(() => {
    return (
      localStorage.getItem(`marketplace_${persona}_selectedCategory`) || ''
    );
  });
  const [selectedStatus, setSelectedStatus] = useState(() => {
    return localStorage.getItem(`marketplace_${persona}_selectedStatus`) || '';
  });
  const [showMyItems, setShowMyItems] = useState(() => {
    return (
      localStorage.getItem(`marketplace_${persona}_showMyItems`) === 'true'
    );
  });
  const [showFilters, setShowFilters] = useState(() => {
    return (
      localStorage.getItem(`marketplace_${persona}_showFilters`) === 'true'
    );
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(12);

  // Fetch categories and statuses
  const { data: categories } = useApiGet(
    ['marketplace-categories'],
    getMarketplaceCategories
  );

  const { data: statuses } = useApiGet(
    ['marketplace-statuses'],
    getMarketplaceStatuses
  );

  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDelayedSearchTerm(searchTerm);
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  // Save filters to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem(`marketplace_${persona}_searchTerm`, searchTerm);
  }, [searchTerm, persona]);

  useEffect(() => {
    localStorage.setItem(
      `marketplace_${persona}_selectedCategory`,
      selectedCategory
    );
  }, [selectedCategory, persona]);

  useEffect(() => {
    localStorage.setItem(
      `marketplace_${persona}_selectedStatus`,
      selectedStatus
    );
  }, [selectedStatus, persona]);

  useEffect(() => {
    localStorage.setItem(
      `marketplace_${persona}_showMyItems`,
      showMyItems.toString()
    );
  }, [showMyItems, persona]);

  useEffect(() => {
    localStorage.setItem(
      `marketplace_${persona}_showFilters`,
      showFilters.toString()
    );
  }, [showFilters, persona]);

  // Fetch marketplace items
  const {
    data: itemsData,
    isLoading,
    error,
  } = useApiGet(
    [
      'marketplace-items',
      currentPage,
      delayedSearchTerm,
      selectedCategory,
      selectedStatus,
      showMyItems,
      persona,
    ],
    () =>
      getMarketplaceItems({
        page: currentPage,
        page_size: itemsPerPage,
        search: delayedSearchTerm || undefined,
        category_id: selectedCategory || undefined,
        status_id: canViewStatusFilter(persona)
          ? selectedStatus || undefined
          : undefined,
        my_items: canViewMyItemsFilter(persona) ? showMyItems : undefined,
        order_by: 'created_at',
        order: 'desc',
      })
  );

  const handleCategoryChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value;
    setSelectedCategory(value);
    setCurrentPage(1); // Reset to first page when changing filters
  };

  const handleStatusChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value;
    setSelectedStatus(value);
    setCurrentPage(1); // Reset to first page when changing filters
  };

  const handleMyItemsToggle = () => {
    setShowMyItems(!showMyItems);
    setCurrentPage(1); // Reset to first page when changing filters
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setDelayedSearchTerm('');
    setSelectedCategory('');
    setSelectedStatus('');
    setShowMyItems(false);
    setCurrentPage(1);

    // Clear localStorage
    localStorage.removeItem(`marketplace_${persona}_searchTerm`);
    localStorage.removeItem(`marketplace_${persona}_selectedCategory`);
    localStorage.removeItem(`marketplace_${persona}_selectedStatus`);
    localStorage.removeItem(`marketplace_${persona}_showMyItems`);
  };

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    page: number
  ) => {
    setCurrentPage(page);
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <AlertItem
          severity="error"
          description="Error loading marketplace items"
          onClose={() => {}}
          keepOpen={true}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Typography variant="h4" component="h1">
          {persona === 'approver'
            ? 'Marketplace Administration'
            : 'Fundraising Marketplace'}
        </Typography>
        <Tooltip
          title={
            persona !== 'approver'
              ? 'Only approvers can create marketplace ads'
              : 'Create a new marketplace ad'
          }
          arrow
        >
          <span>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/marketplace/create')}
              disabled={persona !== 'approver'}
              sx={{
                backgroundColor:
                  persona === 'approver' ? '#FF6B35' : 'rgba(0, 0, 0, 0.12)',
                color: persona === 'approver' ? 'white' : 'rgba(0, 0, 0, 0.26)',
                '&:hover': {
                  backgroundColor:
                    persona === 'approver' ? '#E55A2B' : 'rgba(0, 0, 0, 0.12)',
                },
                '&:disabled': {
                  backgroundColor: 'rgba(0, 0, 0, 0.12)',
                  color: 'rgba(0, 0, 0, 0.26)',
                },
              }}
            >
              Create Ad
            </Button>
          </span>
        </Tooltip>
      </Box>

      {/* Search and Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder="Search items..."
              value={searchTerm}
              size="small"
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              variant={showFilters ? 'contained' : 'outlined'}
              startIcon={<FilterIcon />}
              onClick={() => setShowFilters(!showFilters)}
            >
              Filters
            </Button>
          </Grid>

          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<ClearIcon />}
              onClick={handleClearFilters}
            >
              Clear
            </Button>
          </Grid>
        </Grid>

        {/* Advanced Filters */}
        {showFilters && (
          <Box sx={{ mt: 2 }}>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} md={canViewStatusFilter(persona) ? 6 : 12}>
                <FormControl fullWidth>
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={selectedCategory}
                    onChange={handleCategoryChange}
                    label="Category"
                  >
                    <MenuItem value="">
                      <em>All Categories</em>
                    </MenuItem>
                    {categories?.map((category: any) => (
                      <MenuItem key={category.id} value={category.id}>
                        {category.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              {canViewStatusFilter(persona) && (
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={selectedStatus}
                      onChange={handleStatusChange}
                      label="Status"
                    >
                      <MenuItem value="">
                        <em>All Statuses</em>
                      </MenuItem>
                      {statuses?.map((status: any) => (
                        <MenuItem key={status.id} value={status.id}>
                          {status.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}
              {canViewMyItemsFilter(persona) && (
                <Grid item xs={12}>
                  <Button
                    fullWidth
                    variant={showMyItems ? 'contained' : 'outlined'}
                    onClick={handleMyItemsToggle}
                    sx={{
                      backgroundColor: showMyItems ? '#0072CE' : 'transparent',
                      color: showMyItems ? 'white' : 'inherit',
                      borderColor: showMyItems
                        ? '#0072CE'
                        : 'rgba(0, 0, 0, 0.23)',
                      '&:hover': {
                        backgroundColor: showMyItems
                          ? '#005a9e'
                          : 'rgba(0, 114, 206, 0.04)',
                        borderColor: showMyItems ? '#005a9e' : '#0072CE',
                      },
                    }}
                  >
                    {showMyItems ? '✓ My Items Only' : 'Show My Items Only'}
                  </Button>
                </Grid>
              )}
            </Grid>
          </Box>
        )}
      </Paper>

      {/* Active Filters Display */}
      {(searchTerm || selectedCategory || selectedStatus || showMyItems) && (
        <Paper sx={{ p: 2, mb: 3, backgroundColor: 'action.hover' }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
            Active Filters:
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {searchTerm && (
              <Chip
                label={`Search: "${searchTerm}"`}
                onDelete={() => {
                  setSearchTerm('');
                  setDelayedSearchTerm('');
                }}
                color="primary"
                variant="outlined"
                size="small"
              />
            )}
            {selectedCategory && (
              <Chip
                label={`Category: ${categories?.find((cat: any) => cat.id === selectedCategory)?.name || selectedCategory}`}
                onDelete={() => setSelectedCategory('')}
                color="secondary"
                variant="outlined"
                size="small"
              />
            )}
            {canViewStatusFilter(persona) && selectedStatus && (
              <Chip
                label={`Status: ${statuses?.find((status: any) => status.id === selectedStatus)?.name || selectedStatus}`}
                onDelete={() => setSelectedStatus('')}
                color="secondary"
                variant="outlined"
                size="small"
              />
            )}
            {canViewMyItemsFilter(persona) && showMyItems && (
              <Chip
                label="My Items"
                onDelete={() => setShowMyItems(false)}
                color="primary"
                variant="outlined"
                size="small"
              />
            )}
          </Stack>
        </Paper>
      )}

      {/* Results Count */}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {itemsData?.pagination?.total_count || 0} items found
      </Typography>

      {/* Items Grid */}
      {itemsData?.data?.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" color="text.secondary">
              No items found
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Try adjusting your search criteria or create a new ad.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {itemsData?.data?.map((item: MarketplaceItem) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={item.id}>
              <MarketplaceItemCard
                item={item}
                onClick={() => navigate(`/marketplace/${item.id}`)}
              />
            </Grid>
          ))}
        </Grid>
      )}

      {/* Pagination */}
      {itemsData &&
        itemsData.pagination &&
        itemsData.pagination.total_pages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <Pagination
              count={itemsData.pagination.total_pages}
              page={currentPage}
              onChange={handlePageChange}
              color="primary"
              size="large"
            />
          </Box>
        )}
    </Box>
  );
};

export default MarketplaceList;
